import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useParams, useHistory } from 'react-router-dom';
import usePlans from '../../hooks/usePlans';
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@material-ui/icons';
import { i18n } from '../../translate/i18n';
import { toast } from 'react-toastify';
import ColorPicker from '../../components/ColorPicker';
import Title from '../../components/Title';

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(1),
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  colorPreview: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: '2px solid #ddd',
    cursor: 'pointer',
    marginLeft: theme.spacing(1),
  },
  prerequisitesSection: {
    marginTop: theme.spacing(3),
  },
  prerequisiteItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(1),
    '&:hover': {
      opacity: 0.7,
    },
  },
  prerequisiteHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  prerequisiteTitle: {
    fontWeight: 'bold',
  },
  prerequisiteDescription: {
    color: '#666',
    fontSize: '0.9rem',
  },
  checkboxGroup: {
    marginTop: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  saveButton: {
    '&:hover': {
      opacity: 0.7,
    },
  },
  cancelButton: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
    '&:hover': {
      backgroundColor: theme.palette.grey[400],
    },
  },
}));

const StageConfig = () => {
  const classes = useStyles();
  const { getPlanCompany } = usePlans();

  const { id } = useParams();
  const history = useHistory();

  // Estado do estágio
  const [stage, setStage] = useState({
    id: parseInt(id),
    name: '',
    color: '#2196f3',
    description: '',
    requirements: [],
    pipelineId: null,
  });

  // Estado para armazenar o estágio original (para comparação)
  const [originalStage, setOriginalStage] = useState(null);

  // Estados para pré-requisitos
  const [preRequirements, setPreRequirements] = useState([]);
  const [showPreRequirementForm, setShowPreRequirementForm] = useState(false);
  const [editingPreRequirement, setEditingPreRequirement] = useState(null);
  const [isPreRequirementMode, setIsPreRequirementMode] = useState(false);

  // Estado para controle do color picker
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Estados para gatilhos personalizados
  const [showTriggerForm, setShowTriggerForm] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [isGoogleCalendarEnabled, setIsGoogleCalendarEnabled] = useState(false);
  const [triggerForm, setTriggerForm] = useState({
    tipo_gatilho: '',
    descricao: '',
    obrigatorio: false,
    opcoes: [],
    novaOpcao: '',
  });

  // Carregar dados do estágio
  useEffect(() => {
    const fetchStage = async () => {
      try {
        const { data } = await api.get(`/stages/${id}`);
        // Verificar se data.stage existe e tem a estrutura esperada
        if (data && data.stage) {
          const stageData = {
            ...data.stage,
            requirements: data.stage.requirements || [],
            pipelineId:
              data.stage.pipelineId || data.stage.pipeline?.id || null,
          };
          setStage(stageData);
          setOriginalStage(stageData); // Armazenar o estágio original para comparação

          // Carregar pré-requisitos se for estágio "Perdidos"
          if (
            stageData.name &&
            (stageData.name.toLowerCase().includes('perdido') ||
              stageData.name.toLowerCase().includes('lost')) &&
            stageData.pipelineId
          ) {
            loadPreRequirements(stageData.pipelineId);
          }
        }
      } catch (err) {
        console.error('Erro ao buscar estágio', err);
      }
    };

    fetchStage();
  }, [id]);

  // Verificar se Google Calendar está ativo no plano da empresa
  useEffect(() => {
    const checkGoogleCalendarStatus = async () => {
      try {
        const companyId = localStorage.getItem('companyId');
        if (companyId) {
          const planData = await getPlanCompany({}, companyId);
          if (planData && planData.plan) {
            setIsGoogleCalendarEnabled(
              planData.plan.useGoogleCalendar === true
            );
          }
        }
      } catch (err) {
        console.error('Erro ao verificar status do Google Calendar:', err);
        setIsGoogleCalendarEnabled(false);
      }
    };

    checkGoogleCalendarStatus();
  }, [getPlanCompany]);

  // Carregar pré-requisitos da pipeline
  const loadPreRequirements = async pipelineId => {
    if (!pipelineId) return;
    try {
      const { data } = await api.get(
        `/pipelines/${pipelineId}/lost-stage-pre-requirements`
      );
      setPreRequirements(data.preRequirements || []);
    } catch (error) {
      console.error('Erro ao carregar pré-requisitos:', error);
      setPreRequirements([]);
    }
  };

  // Handlers
  const handleInputChange = (field, value) => {
    setStage(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleColorChange = color => {
    setStage(prev => ({
      ...prev,
      color: color,
    }));
    setShowColorPicker(false);
  };

  // Handlers para gatilhos personalizados
  const handleTriggerFormChange = (field, value) => {
    setTriggerForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddTriggerOption = opcao => {
    if (!triggerForm.opcoes.includes(opcao)) {
      setTriggerForm(prev => ({
        ...prev,
        opcoes: [...prev.opcoes, opcao],
      }));
    }
  };

  const handleRemoveTriggerOption = index => {
    setTriggerForm(prev => ({
      ...prev,
      opcoes: prev.opcoes.filter((_, i) => i !== index),
    }));
  };

  /*
  const handleSaveTrigger = async () => {
    if (!triggerForm.descricao.trim()) {
      toast.error('Descrição da atividade é obrigatória');
      return;
    }

    if (!triggerForm.tipo_gatilho) {
      toast.error('Tipo do campo é obrigatório');
      return;
    }

    if (
      triggerForm.tipo_gatilho === 'select' &&
      triggerForm.opcoes.length === 0
    ) {
      toast.error('Campo do tipo select precisa ter pelo menos uma opção');
      return;
    }

    try {
      // Mapear tipos para valores da API
      const apiType =
        triggerForm.tipo_gatilho === 'venda'
          ? 'sale'
          : triggerForm.tipo_gatilho === 'anexos'
            ? 'attach'
            : triggerForm.tipo_gatilho === 'agendamentos'
              ? 'schedule'
              : triggerForm.tipo_gatilho;

      const response = await api.post('/requirements', {
        name: triggerForm.descricao,
        type: apiType, // valores: checkbox, select, text, date, sale, attach, schedule
        options: triggerForm.opcoes,
        required: triggerForm.obrigatorio,
        order: 0,
        stageIds: [], // será vinculado depois via patch
      });

      const newRequirement = response.data;
      toast.success('Atividade criada com sucesso!');

      // atualizar local state
      setStage(prev => ({
        ...prev,
        requirements: [...(prev.requirements || []), newRequirement],
      }));

      handleCancelTriggerForm();
    } catch (err) {
      toast.error('Erro ao criar atividade');
    }
  };
  */

  const handleEditTrigger = trigger => {
    setEditingTrigger(trigger);

    // Mapear valores da API para tipos do formulário
    const formType =
      trigger.type === 'sale'
        ? 'venda'
        : trigger.type === 'attach'
          ? 'anexos'
          : trigger.type === 'schedule'
            ? 'agendamentos'
            : trigger.type;

    setTriggerForm({
      tipo_gatilho: formType || '',
      descricao: trigger.name || '',
      obrigatorio: trigger.required || false,
      opcoes: trigger.options || [],
      novaOpcao: '',
    });
    setShowTriggerForm(true);
  };

  const handleDeleteTrigger = async triggerId => {
    try {
      // chamada ao backend
      await api.delete(`/requirements/${triggerId}`);

      // atualizar estado local
      setStage(prev => ({
        ...prev,
        requirements: (prev.requirements || []).filter(r => r.id !== triggerId),
      }));

      toast.success('Atividade removida com sucesso!');
    } catch (err) {
      console.error('Erro ao excluir requirement:', err);
      toast.error('Erro ao excluir atividade');
    }
  };

  const handleCancelTriggerForm = () => {
    setShowTriggerForm(false);
    setShowPreRequirementForm(false);
    setEditingTrigger(null);
    setEditingPreRequirement(null);
    setIsPreRequirementMode(false);
    setTriggerForm({
      tipo_gatilho: '',
      descricao: '',
      obrigatorio: false,
      opcoes: [],
      novaOpcao: '',
    });
  };

  // Handlers para pré-requisitos
  const handleAddPreRequirement = () => {
    setIsPreRequirementMode(true);
    setShowPreRequirementForm(true);
    setEditingPreRequirement(null);
    setTriggerForm({
      tipo_gatilho: '',
      descricao: '',
      obrigatorio: false,
      opcoes: [],
      novaOpcao: '',
    });
  };

  const handleEditPreRequirement = preReq => {
    setIsPreRequirementMode(true);
    setShowPreRequirementForm(true);
    setEditingPreRequirement(preReq);

    const requirement = preReq.requirement || preReq;
    const formType =
      requirement.type === 'sale'
        ? 'venda'
        : requirement.type === 'attach'
          ? 'anexos'
          : requirement.type === 'schedule'
            ? 'agendamentos'
            : requirement.type;

    setTriggerForm({
      tipo_gatilho: formType || '',
      descricao: requirement.name || '',
      obrigatorio: requirement.required || false,
      opcoes: requirement.options || [],
      novaOpcao: '',
    });
  };

  const handleDeletePreRequirement = async requirementId => {
    try {
      await api.delete(
        `/pipelines/${stage.pipelineId}/lost-stage-pre-requirements/${requirementId}`
      );
      toast.success('Pré-requisito removido com sucesso!');
      await loadPreRequirements(stage.pipelineId);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erro ao remover pré-requisito';
      toast.error(errorMessage);
    }
  };

  // Verificar se é estágio "Perdidos"
  const isLostStage =
    stage.name &&
    (stage.name.toLowerCase().includes('perdido') ||
      stage.name.toLowerCase().includes('lost'));

  const handleSave = async () => {
    // Validar campos obrigatórios
    if (!stage.name.trim()) {
      toast.error(i18n.t('stageConfig.errors.nameRequired'));
      return;
    }

    try {
      // Verificar se houve alterações no nome ou cor
      if (!originalStage) {
        toast.error('Erro: Dados originais do estágio não encontrados');
        return;
      }

      const updateData = {};

      // Comparar nome
      if (stage.name !== originalStage.name) {
        updateData.name = stage.name;
      }

      // Comparar descrição
      if (stage.description !== originalStage.description) {
        updateData.description = stage.description;
      }

      // Comparar cor
      if (stage.color !== originalStage.color) {
        updateData.color = stage.color;
      }

      // Só fazer a requisição se houver alterações
      if (Object.keys(updateData).length > 0) {
        await api.patch(`/stages/${stage.id}`, updateData);
        toast.success(i18n.t('stageConfig.saved'));
      } else {
        toast.info('Nenhuma alteração foi feita');
      }

      // Voltar para a página anterior
      history.goBack();
    } catch (err) {
      toast.error('Erro ao salvar alterações do estágio');
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  return (
    <Container className={classes.container} maxWidth='md'>
      {/* Header */}
      <Box className={classes.header}>
        <IconButton
          className={classes.backButton}
          onClick={() => history.goBack()}
          aria-label={i18n.t('common.back')}
        >
          <ArrowBackIcon />
        </IconButton>
        <Title>
          {i18n.t('stageConfig.title')} - {stage.name}
        </Title>
      </Box>

      {/* Formulário Principal */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant='h6' className={classes.sectionTitle}>
            {i18n.t('stageConfig.basicInfo')}
          </Typography>

          {/* Nome do Estágio */}
          <TextField
            fullWidth
            label={i18n.t('stageConfig.name')}
            value={stage.name}
            onChange={e => handleInputChange('name', e.target.value)}
            className={classes.formField}
            required
          />

          {/* Descrição */}
          <TextField
            fullWidth
            label={i18n.t('stageConfig.description')}
            value={stage.description}
            onChange={e => handleInputChange('description', e.target.value)}
            className={classes.formField}
            multiline
            rows={3}
          />

          {/* Cor */}
          <Box display='flex' alignItems='center' className={classes.formField}>
            <Typography variant='body1' style={{ marginRight: 16 }}>
              {i18n.t('stageConfig.color')}:
            </Typography>
            <div
              className={classes.colorPreview}
              style={{ backgroundColor: stage.color }}
              onClick={() => setShowColorPicker(true)}
              aria-label={i18n.t('stageConfig.selectColor')}
            />
            {showColorPicker && (
              <ColorPicker
                currentColor={stage.color}
                onChange={handleColorChange}
                handleClose={() => setShowColorPicker(false)}
                open={showColorPicker}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Atividades do Estágio ou Pré-requisitos (se for estágio Perdidos) */}
      {isLostStage && stage.pipelineId ? (
        /* Pré-requisitos para Estágio "Perdidos" */
        <Card className={classes.card}>
          <CardContent>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mb={2}
            >
              <Box>
                <Typography variant='h6' className={classes.sectionTitle}>
                  <SettingsIcon />
                  Pré-requisitos
                </Typography>
                <Typography
                  variant='body2'
                  color='textSecondary'
                  style={{ marginTop: 8 }}
                >
                  Configure os pré-requisitos que devem ser preenchidos antes de
                  mover um atendimento para este estágio.
                </Typography>
              </Box>
              <Button
                variant='contained'
                color='primary'
                startIcon={<AddIcon />}
                onClick={handleAddPreRequirement}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    lineHeight: 1.2,
                  }}
                >
                  <span>Adicionar</span>
                  <span>Pré-requisito</span>
                </div>
              </Button>
            </Box>

            {preRequirements.length > 0 ? (
              preRequirements.map(preReq => {
                const requirement = preReq.requirement || preReq;
                return (
                  <Paper
                    key={preReq.id || requirement.id}
                    className={classes.prerequisiteItem}
                    style={{ marginBottom: 12 }}
                  >
                    <Box
                      display='flex'
                      justifyContent='space-between'
                      alignItems='center'
                    >
                      <Box flex={1}>
                        <Box display='flex' alignItems='center'>
                          <Typography className={classes.prerequisiteTitle}>
                            {requirement.name}
                          </Typography>
                          {requirement.required && (
                            <Chip
                              size='small'
                              label='Obrigatório'
                              color='secondary'
                              style={{ marginLeft: 8 }}
                            />
                          )}
                        </Box>

                        <Typography className={classes.prerequisiteDescription}>
                          Tipo:{' '}
                          {requirement.type === 'checkbox'
                            ? i18n.t('stageConfig.activityTypes.checkbox')
                            : requirement.type === 'select'
                              ? i18n.t('stageConfig.activityTypes.select')
                              : requirement.type === 'text'
                                ? i18n.t('stageConfig.activityTypes.text')
                                : requirement.type === 'date'
                                  ? i18n.t('stageConfig.activityTypes.date')
                                  : requirement.type === 'sale'
                                    ? i18n.t('stageConfig.activityTypes.sale')
                                    : requirement.type === 'attach'
                                      ? i18n.t(
                                          'stageConfig.activityTypes.attach'
                                        )
                                      : requirement.type === 'schedule'
                                        ? i18n.t(
                                            'stageConfig.activityTypes.schedule'
                                          )
                                        : requirement.type}
                          {requirement.type === 'select' &&
                            requirement.options &&
                            requirement.options.length > 0 && (
                              <> - Opções: {requirement.options.join(', ')}</>
                            )}
                        </Typography>
                      </Box>

                      <Box>
                        <IconButton
                          size='small'
                          onClick={() => handleEditPreRequirement(preReq)}
                          color='primary'
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size='small'
                          onClick={() =>
                            handleDeletePreRequirement(
                              preReq.requirementId || requirement.id
                            )
                          }
                          color='secondary'
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                );
              })
            ) : (
              <Typography
                variant='body2'
                color='textSecondary'
                style={{
                  fontStyle: 'italic',
                  textAlign: 'center',
                  padding: '20px',
                }}
              >
                Nenhum pré-requisito configurado. Adicione um para exigir o
                preenchimento antes de mover deals para "Perdidos".
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Atividades do Estágio (para estágios que não são Perdidos) */
        <Card className={classes.card}>
          <CardContent>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mb={2}
            >
              <Typography variant='h6' className={classes.sectionTitle}>
                <SettingsIcon />
                Atividades do Estágio
              </Typography>
              <Button
                variant='contained'
                color='primary'
                startIcon={<AddIcon />}
                onClick={() => {
                  setIsPreRequirementMode(false);
                  setShowTriggerForm(true);
                }}
              >
                Adicionar Atividade
              </Button>
            </Box>

            <Typography
              variant='body2'
              color='textSecondary'
              style={{ marginBottom: 16 }}
            >
              Configure as atividades que devem ser realizadas neste estágio.
              Você pode criar diferentes tipos de campos para coletar
              informações importantes.
            </Typography>

            {(stage.requirements || []).length === 0 ? (
              <Typography
                variant='body2'
                color='textSecondary'
                style={{
                  fontStyle: 'italic',
                  textAlign: 'center',
                  padding: '20px',
                }}
              >
                Nenhuma atividade configurada para este estágio. Clique em
                "Adicionar Atividade" para criar a primeira.
              </Typography>
            ) : (
              (stage.requirements || []).map(requirement => (
                <Paper
                  key={requirement.id}
                  className={classes.prerequisiteItem}
                  style={{ marginBottom: 12 }}
                >
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <Box flex={1}>
                      <Box display='flex' alignItems='center'>
                        <Typography className={classes.prerequisiteTitle}>
                          {requirement.name}
                        </Typography>
                        {requirement.required && (
                          <Chip
                            size='small'
                            label='Obrigatório'
                            color='secondary'
                            style={{ marginLeft: 8 }}
                          />
                        )}
                      </Box>

                      <Typography className={classes.prerequisiteDescription}>
                        Tipo:{' '}
                        {requirement.type === 'checkbox'
                          ? i18n.t('stageConfig.activityTypes.checkbox')
                          : requirement.type === 'select'
                            ? i18n.t('stageConfig.activityTypes.select')
                            : requirement.type === 'text'
                              ? i18n.t('stageConfig.activityTypes.text')
                              : requirement.type === 'date'
                                ? i18n.t('stageConfig.activityTypes.date')
                                : requirement.type === 'sale'
                                  ? i18n.t('stageConfig.activityTypes.sale')
                                  : requirement.type === 'attach'
                                    ? i18n.t('stageConfig.activityTypes.attach')
                                    : requirement.type === 'schedule'
                                      ? i18n.t(
                                          'stageConfig.activityTypes.schedule'
                                        )
                                      : requirement.type}
                        {requirement.type === 'select' &&
                          requirement.options &&
                          requirement.options.length > 0 && (
                            <> - Opções: {requirement.options.join(', ')}</>
                          )}
                      </Typography>
                    </Box>

                    <Box>
                      <IconButton
                        size='small'
                        onClick={() => handleEditTrigger(requirement)}
                        color='primary'
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size='small'
                        onClick={() => handleDeleteTrigger(requirement.id)}
                        color='secondary'
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal para Criar/Editar Requirement */}
      <Dialog
        open={showTriggerForm || showPreRequirementForm}
        onClose={handleCancelTriggerForm}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {isPreRequirementMode
            ? editingPreRequirement
              ? 'Editar pré-requisito'
              : 'Criar novo pré-requisito'
            : editingTrigger
              ? 'Editar Atividade'
              : 'Criar Nova Atividade'}
        </DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2} mt={1}>
            {/* Descrição */}
            <TextField
              label={
                isPreRequirementMode
                  ? 'Descrição do pré-requisito'
                  : 'Descrição da atividade'
              }
              value={triggerForm.descricao}
              onChange={e =>
                handleTriggerFormChange('descricao', e.target.value)
              }
              fullWidth
              required
            />

            {/* Tipo */}
            <FormControl fullWidth required>
              <InputLabel>Tipo de Campo</InputLabel>
              <Select
                value={triggerForm.tipo_gatilho}
                onChange={e =>
                  handleTriggerFormChange('tipo_gatilho', e.target.value)
                }
              >
                <MenuItem value='checkbox'>
                  {i18n.t('stageConfig.activityTypes.checkbox')}
                </MenuItem>
                <MenuItem value='select'>
                  {i18n.t('stageConfig.activityTypes.select')}
                </MenuItem>
                <MenuItem value='text'>
                  {i18n.t('stageConfig.activityTypes.text')}
                </MenuItem>
                <MenuItem value='date'>
                  {i18n.t('stageConfig.activityTypes.date')}
                </MenuItem>
                <MenuItem value='venda'>
                  {i18n.t('stageConfig.activityTypes.sale')}
                </MenuItem>
                <MenuItem value='anexos'>
                  {i18n.t('stageConfig.activityTypes.attach')}
                </MenuItem>
              </Select>
            </FormControl>

            {/* Opções para Select */}
            {triggerForm.tipo_gatilho === 'select' && (
              <Box>
                <Typography variant='subtitle2' mb={1}>
                  Opções do Select
                </Typography>

                {/* Input e botão para adicionar nova opção */}
                <Box display='flex' gap={1} mb={2}>
                  <TextField
                    label='Nova opção'
                    value={triggerForm.novaOpcao || ''}
                    onChange={e =>
                      handleTriggerFormChange('novaOpcao', e.target.value)
                    }
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (triggerForm.novaOpcao?.trim()) {
                          handleAddTriggerOption(triggerForm.novaOpcao.trim());
                          handleTriggerFormChange('novaOpcao', '');
                        }
                      }
                    }}
                    size='small'
                  />
                  <Button
                    variant='outlined'
                    onClick={() => {
                      if (triggerForm.novaOpcao?.trim()) {
                        handleAddTriggerOption(triggerForm.novaOpcao.trim());
                        handleTriggerFormChange('novaOpcao', '');
                      }
                    }}
                  >
                    Adicionar
                  </Button>
                </Box>

                {/* Listagem das opções adicionadas */}
                <Box display='flex' flexWrap='wrap'>
                  {triggerForm.opcoes.map((opcao, index) => (
                    <Chip
                      key={index}
                      label={opcao}
                      onDelete={() => handleRemoveTriggerOption(index)}
                      color='primary'
                      variant='outlined'
                      style={{ margin: '4px' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Campo Obrigatório */}
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={triggerForm.obrigatorio}
                    onChange={e =>
                      handleTriggerFormChange('obrigatorio', e.target.checked)
                    }
                  />
                }
                label='Campo Obrigatório'
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTriggerForm}>Cancelar</Button>
          <Button
            onClick={async () => {
              try {
                // Validações
                if (!triggerForm.descricao.trim()) {
                  toast.error('Descrição da atividade é obrigatória');
                  return;
                }
                if (!triggerForm.tipo_gatilho) {
                  toast.error('Tipo do campo é obrigatório');
                  return;
                }
                if (
                  triggerForm.tipo_gatilho === 'select' &&
                  triggerForm.opcoes.length === 0
                ) {
                  toast.error(
                    'Campo do tipo select precisa ter pelo menos uma opção'
                  );
                  return;
                }

                if (editingTrigger || editingPreRequirement) {
                  const requirementToEdit =
                    editingTrigger ||
                    editingPreRequirement?.requirement ||
                    editingPreRequirement;
                  // Mapear tipos para valores da API
                  const apiType =
                    triggerForm.tipo_gatilho === 'venda'
                      ? 'sale'
                      : triggerForm.tipo_gatilho === 'anexos'
                        ? 'attach'
                        : triggerForm.tipo_gatilho === 'agendamentos'
                          ? 'schedule'
                          : triggerForm.tipo_gatilho;

                  const { data: updatedRequirement } = await api.patch(
                    `/requirements/${requirementToEdit.id}`,
                    {
                      name: triggerForm.descricao,
                      type: apiType,
                      required: triggerForm.obrigatorio,
                      options: triggerForm.opcoes,
                    }
                  );

                  if (isPreRequirementMode) {
                    // Atualizar pré-requisitos
                    await loadPreRequirements(stage.pipelineId);
                    toast.success('Pré-requisito atualizado com sucesso!');
                  } else {
                    // Atualiza state local
                    setStage(prev => ({
                      ...prev,
                      requirements: (prev.requirements || []).map(r =>
                        r.id === updatedRequirement.id ? updatedRequirement : r
                      ),
                    }));
                    toast.success('Atividade atualizada com sucesso!');
                  }
                } else {
                  // Criar requirement via API
                  // Mapear tipos para valores da API
                  const apiType =
                    triggerForm.tipo_gatilho === 'venda'
                      ? 'sale'
                      : triggerForm.tipo_gatilho === 'anexos'
                        ? 'attach'
                        : triggerForm.tipo_gatilho === 'agendamentos'
                          ? 'schedule'
                          : triggerForm.tipo_gatilho;

                  const { data: newRequirement } = await api.post(
                    '/requirements',
                    {
                      name: triggerForm.descricao,
                      type: apiType,
                      options: triggerForm.opcoes,
                      required: triggerForm.obrigatorio,
                      order: 0,
                      stageIds: [],
                    }
                  );

                  // Se for modo pré-requisito, adicionar como pré-requisito
                  if (isPreRequirementMode && stage.pipelineId) {
                    await api.post(
                      `/pipelines/${stage.pipelineId}/lost-stage-pre-requirements`,
                      {
                        requirementId: newRequirement.id,
                      }
                    );
                    toast.success(
                      'Pré-requisito criado e adicionado com sucesso!'
                    );
                    await loadPreRequirements(stage.pipelineId);
                  } else {
                    // Caso contrário, adicionar como atividade normal do estágio
                    await api.patch(`/stages/${stage.id}/requirements`, {
                      requirementIds: [newRequirement.id],
                    });

                    setStage(prev => ({
                      ...prev,
                      requirements: [
                        ...(prev.requirements || []),
                        newRequirement,
                      ],
                    }));

                    toast.success(
                      'Atividade criada e adicionada ao estágio com sucesso!'
                    );
                  }
                }

                handleCancelTriggerForm();
              } catch (err) {
                console.error(err);
                const backendMsg =
                  err?.response?.data?.message ||
                  err?.response?.data?.error ||
                  (typeof err?.response?.data === 'string'
                    ? err.response.data
                    : null);

                toast.error(backendMsg || 'Erro ao salvar a atividade');
              }
            }}
            variant='contained'
            color='primary'
          >
            {editingTrigger ? 'Atualizar' : 'Criar'} Atividade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ações */}
      <Box className={classes.actions}>
        <Button
          variant='contained'
          className={classes.cancelButton}
          onClick={handleCancel}
        >
          {i18n.t('common.cancel')}
        </Button>
        <Button
          variant='contained'
          className={classes.saveButton}
          startIcon={<SaveIcon />}
          onClick={handleSave}
        >
          {i18n.t('stageConfig.save')}
        </Button>
      </Box>
    </Container>
  );
};

export default StageConfig;
